import {
  all,
  call,
  fork,
  put,
  race,
  select,
  spawn,
  take,
  takeEvery,
} from 'redux-saga/effects'

import * as Storage from '@app/lib/storage'
import type { AppState, AppAction} from '@app/state';
import { bootTimeSagas } from '@app/state'
import * as Auth from '@app/state/authenticate'
import * as Config from '@app/state/config'
import * as Init from '@app/state/init'
import * as User from '@app/state/user'

import { InitError } from './init_error'

export function* beginInit() {
  yield all(Object.keys(Config.sagas).map((k) => spawn(Config.sagas[k])))
  yield spawn(spawnTransitions)
  yield put(Init.actions.start())
}

export function* spawnTransitions() {
  yield takeEvery(Init.ActionType.Start, transitionFromStart)
  yield takeEvery(Init.ActionType.RehydrateComplete, transitionFromRehydrate)
  yield takeEvery(Init.ActionType.ConfigureComplete, transitionFromConfigure)
  yield takeEvery(Init.ActionType.BootComplete, transitionFromBoot)
}

export function* transitionFromStart() {
  yield fork(startInitialization)
  yield put(Init.actions.rehydrate())
}

export function* transitionFromRehydrate() {
  yield put(Init.actions.configure())
}

export function* transitionFromConfigure() {
  yield put(Init.actions.boot())
}

export function* transitionFromBoot() {
  const state = yield select((a) => a)
  if (Auth.queries.isAuthenticated(state.auth)) {
    yield put(User.actions.getAccountInfo())
  }
  yield put(Init.actions.initialized())
}

export function* startInitialization() {
  try {
    yield call(startInitializationHandlers)
  } catch (e) {
    //add error handling
  }
}

export function* startInitializationHandlers() {
  yield fork(handleRehydrateStage)
  yield fork(handleConfigureStage)
  yield fork(handleBootStage)
  yield fork(handleStagesComplete)
}

export function* handleRehydrateStage() {
  yield take(Init.ActionType.Rehydrate)
  yield call(rehydrateAuthToken)
}

export function* handleConfigureStage() {
  yield take(Init.ActionType.Configure)
  yield call(fetchClientConfig)
  yield call(fetchApiConfig)
  yield call(fetchClinicConfig)
}

export function* handleBootStage() {
  yield take(Init.ActionType.Boot)
  yield call(boot)
}

export function* handleStagesComplete() {
  yield take(Init.ActionType.Initialized)
}

export function* rehydrateAuthToken() {
  const tokenExpiryDate = yield call(
    Storage.getItem,
    Storage.API_TOKEN_EXPIRY_DATE,
  )
  const tokenValue = yield call(Storage.getItem, Storage.API_TOKEN_VALUE)
  const identifierValue = yield call(
    Storage.getItem,
    Storage.API_USER_IDENTIFIER,
  )

  const role = yield call(Storage.getItem, Storage.AUTH_USER_ROLE)

  yield put(
    Auth.actions.restoreAuthToken(
      identifierValue,
      tokenValue,
      tokenExpiryDate,
      role,
    ),
  )
  yield put(Init.actions.rehydrateComplete())
}

export function* fetchClientConfig() {
  yield put(Config.actions.fetchClientConfig())

  const { success, failure }: { [key: string]: AppAction } = yield race({
    success: take(Config.ActionType.FetchClientConfigSuccess),
    failure: take(Config.ActionType.FetchClientConfigFailure),
  })

  if (success && success.type === Config.ActionType.FetchClientConfigSuccess) {
    // Continue to fetch config. Note: if this condition is removed, the config saga won't complete
  } else if (
    failure &&
    failure.type === Config.ActionType.FetchClientConfigFailure
  ) {
    throw new InitError(Init.ErrorState.ConfigLocationUnreachable)
  } else {
    throw new InitError(Init.ErrorState.Unknown)
  }
}

export function* fetchApiConfig() {
  yield put(Config.actions.fetchApiConfig())

  const { success, failure }: { [key: string]: AppAction } = yield race({
    success: take(Config.ActionType.FetchApiConfigSuccess),
    failure: take(Config.ActionType.FetchApiConfigFailure),
  })

  if (success && success.type === Config.ActionType.FetchApiConfigSuccess) {
    // Continue to fetch config. Note: if this condition is removed, the config saga won't complete
  } else if (
    failure &&
    failure.type === Config.ActionType.FetchApiConfigFailure
  ) {
    throw new InitError(Init.ErrorState.ConfigUnreachable)
  } else {
    throw new InitError(Init.ErrorState.Unknown)
  }
}

export function* fetchClinicConfig() {
  yield put(Config.actions.fetchClinicConfig())

  const { success, failure }: { [key: string]: AppAction } = yield race({
    success: take(Config.ActionType.FetchClinicConfigSuccess),
    failure: take(Config.ActionType.FetchClinicConfigFailure),
  })

  if (success && success.type === Config.ActionType.FetchClinicConfigSuccess) {
    yield put(Init.actions.configureComplete())
  } else if (
    failure &&
    failure.type === Config.ActionType.FetchClinicConfigFailure
  ) {
    throw new InitError(Init.ErrorState.ConfigUnreachable)
  } else {
    throw new InitError(Init.ErrorState.Unknown)
  }
}

export function* boot() {
  const appState: AppState = yield select((a) => a)
  if (!appState.init.hasBooted) {
    const bootSagas = yield call(getBootTimeSagas)
    yield all(bootSagas)
  }
  yield put(Init.actions.bootComplete())
}

export function getBootTimeSagas() {
  return Object.keys(bootTimeSagas).map((k) => spawn(bootTimeSagas[k]))
}
